import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { assetsAPI, organizationAPI, authAPI, subscriptionAPI, discountAPI, decodeTokenPayload } from './services/api';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Pages
import LandingPage from './pages/public/LandingPage';
import ModernLoginPage from './pages/auth/ModernLoginPage';
import ModernSignupPage from './pages/auth/ModernSignupPage';
import OTPVerificationPage from './pages/auth/OTPVerificationPage';
import RegisterPage from './pages/auth/RegisterPage';
import InvitationAcceptPage from './pages/auth/InvitationAcceptPage';
import OrganizationSetupPage from './pages/setup/OrganizationSetupPage';
import ModernAssetSetupPage from './pages/setup/ModernAssetSetupPage';

// Analytics
import SummaryPage from './pages/analytics/SummaryPage';
import DetailedAnalyticsPage from './pages/analytics/PageViewsPage';
import PageDetailView from './pages/analytics/PageDetailView';
import TransactionsPage from './pages/analytics/TransactionsPage';
import ReportPage from './pages/analytics/ReportPage';
import CampaignsPage from './pages/analytics/CampaignsPage';
import CampaignDetailPage from './pages/analytics/CampaignDetailPage';
import VisitorLogPage from './pages/analytics/VisitorLogPage';
import EventsPage from './pages/analytics/EventsPage';
// App analytics (mobile app integration)
import AppSetupPage from './pages/app-analytics/AppSetupPage';
import AppOverviewPage from './pages/app-analytics/AppOverviewPage';
import AppFunnelPage from './pages/app-analytics/AppFunnelPage';
import AppUsersPage from './pages/app-analytics/AppUsersPage';
import AppUserTimelinePage from './pages/app-analytics/AppUserTimelinePage';
import AppIntegrationDocsPage from './pages/app-analytics/AppIntegrationDocsPage';

// Monitoring
import LiveMonitoringPage from './pages/monitoring/LiveMonitoringPage';
import PerformanceReportPage from './pages/monitoring/PerformanceReportPage';
import LiveHubPage from './pages/monitoring/LiveHubPage';

// Security
import SecuritySummaryPage from './pages/security/SecuritySummaryPage';
import SecurityScanningPage from './pages/security/SecurityScanningPage';
import SecurityScanProgressPage from './pages/security/SecurityScanProgressPage';
import SecurityVulnerabilitiesPage from './pages/security/SecurityVulnerabilitiesPage';
import ScanResultsPage from './pages/security/ScanResultsPage';
import AISecurityAdvisorPage from './pages/security/AISecurityAdvisorPage';
import AiSecurityAuditPage from './pages/security/AiSecurityAuditPage';
import AiAssistantPage from './pages/security/AiAssistantPage';
import CodeScanDashboardPage from './pages/security/codescan/CodeScanDashboardPage';
import CodeScanTargetsPage from './pages/security/codescan/CodeScanTargetsPage';
import CodeScanResultDetailPage from './pages/security/codescan/CodeScanResultDetailPage';

// Integration
import IntegrationPage from './pages/integration/IntegrationPage';
import ShareDataPage from './pages/integration/ShareDataPage';

// Users
import UserManagementPage from './pages/users/UserManagementPage';

// Organisation
import OrganisationProfilePage from './pages/organisation/OrganisationProfilePage';
import RegisterOrganisationPage from './pages/organisation/RegisterOrganisationPage';

// Assets
import AssetsListPage from './pages/assets/AssetsListPage';
import AssetDetailPage from './pages/assets/AssetDetailPage';

// Server Monitoring
import ServersListPage from './pages/servers/ServersListPage';
import ServerRegistrationPage from './pages/servers/ServerRegistrationPage';
import ServerDetailPage from './pages/servers/ServerDetailPage';
import FileBrowserPage from './pages/servers/FileBrowserPage';
import EndpointsListPage from './pages/endpoints/EndpointsListPage';
import EndpointAddPage from './pages/endpoints/EndpointAddPage';
import EndpointDetailPage from './pages/endpoints/EndpointDetailPage';
import EndpointPolicyPage from './pages/endpoints/EndpointPolicyPage';
import EndpointThreatsPage from './pages/endpoints/EndpointThreatsPage';

// Alerts
import AlertSendHistoryPage from './pages/alerts/AlertSendHistoryPage';

// Email Security Monitoring
import EmailMonitorDashboardPage from './pages/email-monitor/EmailMonitorDashboardPage';
import EmailDnsRecordsPage from './pages/email-monitor/EmailDnsRecordsPage';
import EmailDnsHistoryPage from './pages/email-monitor/EmailDnsHistoryPage';
import EmailBlacklistPage from './pages/email-monitor/EmailBlacklistPage';

// Event Points
import EventPointsPage from './pages/events/EventPointsPage';
import EventPointDetailPage from './pages/events/EventPointDetailPage';

// Home Dashboard
import MainDashboardPage from './pages/home/MainDashboardPage';

// Feedback
import FeedbackPage from './pages/feedback/FeedbackPage';
import FeedbackWidgetPage from './pages/integration/FeedbackWidgetPage';

// SEO
import SeoSummaryPage from './pages/seo/SeoSummaryPage';
import SeoScannerPage from './pages/seo/SeoScannerPage';
import SeoOptimizerPage from './pages/seo/SeoOptimizerPage';
import NotificationSettingsPage from './pages/settings/NotificationSettingsPage';
import OnboardingOverviewPage from './pages/onboarding/OnboardingOverviewPage';
import OnboardingAssetPage from './pages/onboarding/OnboardingAssetPage';
import KeywordMonitorPage from './pages/seo/KeywordMonitorPage';
import SeoForAIPage from './pages/seo/SeoForAIPage';

// Billing
import BillingPage from './pages/billing/BillingPage';
import PlansPage from './pages/billing/PlansPage';
import CheckoutPage from './pages/billing/CheckoutPage';
import PaymentHistoryPage from './pages/billing/PaymentHistoryPage';
import ReceiptPage from './pages/billing/ReceiptPage';

// Legal
import TermsPage from './pages/legal/TermsPage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import RefundPolicyPage from './pages/legal/RefundPolicyPage';

// Blog
import BlogIndexPage from './pages/public/BlogIndexPage';
import BlogPostPage from './pages/public/BlogPostPage';

// Simple Loading Component
const LoadingSpinner: React.FC = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Protected Route - requires login (supports embed token auto-auth via ?token=xxx&embed=true)
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiresAsset?: boolean }> = ({
  children,
  requiresAsset = false
}) => {
  const { user, assets, organizations, isLoading, login, setAssets, setCurrentAsset, setOrganizations, setCurrentOrganization, subscriptionStatus, pendingPlanId, pendingPeriod } = useAuth();
  const [searchParams] = useSearchParams();
  const [checkingAssets, setCheckingAssets] = useState(false);
  const [hasVerifiedAssets, setHasVerifiedAssets] = useState<boolean | null>(null);
  const [embedAuthDone, setEmbedAuthDone] = useState(false);

  // Embed token auto-authentication
  useEffect(() => {
    const token = searchParams.get('token');
    const isEmbed = searchParams.get('embed') === 'true';
    if (!token || !isEmbed || user) {
      setEmbedAuthDone(true);
      return;
    }
    try {
      const payload = decodeTokenPayload(token);
      if (!payload || (payload.exp && payload.exp * 1000 < Date.now())) {
        setEmbedAuthDone(true);
        return;
      }
      localStorage.setItem('auth_token', token);
      login(
        { id: payload.user_id, name: payload.name || 'Embed User', email: payload.email || '' },
        token
      );
      if (payload.asset_id) {
        assetsAPI.list().then((response) => {
          if (response.data.status === 'success' && response.data.data) {
            const fetchedAssets = response.data.data.assets || [];
            setAssets(fetchedAssets);
            const targetAsset = fetchedAssets.find((a: any) => a.id === payload.asset_id) || fetchedAssets[0];
            if (targetAsset) setCurrentAsset(targetAsset);
          }
          setEmbedAuthDone(true);
        }).catch(() => setEmbedAuthDone(true));
      } else {
        setEmbedAuthDone(true);
      }
    } catch {
      setEmbedAuthDone(true);
    }
  }, [searchParams, user, login, setAssets, setCurrentAsset]);

  // Fetch organizations if not loaded
  useEffect(() => {
    if (user && organizations.length === 0) {
      organizationAPI.list()
        .then((response) => {
          if (response.data.status === 'success' && response.data.data?.organizations) {
            const orgs = response.data.data.organizations.map((org: any) => ({
              id: org.id,
              name: org.name,
              role: org.role || 'member',
              is_default: !!org.is_default,
            }));
            if (orgs.length > 0) {
              setOrganizations(orgs);
              const defaultOrg = orgs.find((o: any) => o.is_default) || orgs[0];
              setCurrentOrganization(defaultOrg);
            }
          }
        })
        .catch((error) => {
          console.error('Failed to fetch organizations:', error);
        });
    }
  }, [user, organizations.length, setOrganizations, setCurrentOrganization]);

  useEffect(() => {
    // If route requires assets and we don't have them in state, fetch from API
    if (requiresAsset && user && assets.length === 0 && hasVerifiedAssets === null) {
      setCheckingAssets(true);

      assetsAPI.list()
        .then((response) => {
          if (response.data.status === 'success' && response.data.data) {
            const fetchedAssets = response.data.data.assets || [];
            if (fetchedAssets.length > 0) {
              setAssets(fetchedAssets);
              setCurrentAsset(fetchedAssets[0]);
              setHasVerifiedAssets(true);
            } else {
              setHasVerifiedAssets(false);
            }
          } else {
            setHasVerifiedAssets(false);
          }
        })
        .catch(() => {
          setHasVerifiedAssets(false);
        })
        .finally(() => {
          setCheckingAssets(false);
        });
    }
  }, [requiresAsset, user, assets.length, hasVerifiedAssets, setAssets, setCurrentAsset]);

  if (isLoading || checkingAssets || !embedAuthDone) return <LoadingSpinner />;

  if (!user) {
    // Come back here after logging in (e.g. a docs or onboarding deep link).
    const here = window.location.pathname + window.location.search + window.location.hash;
    return <Navigate to={here && here !== '/' ? `/login?redirect=${encodeURIComponent(here)}` : '/login'} replace />;
  }

  // If this route requires an asset
  if (requiresAsset) {
    // If we have assets in state, allow access
    if (assets.length > 0) {
      return <>{children}</>;
    }

    // If we've verified and have assets, allow access
    if (hasVerifiedAssets === true) {
      return <>{children}</>;
    }

    // If we've verified and no assets, redirect to setup
    if (hasVerifiedAssets === false) {
      return <Navigate to="/setup/asset" replace />;
    }

    // Still checking, show loading
    return <LoadingSpinner />;
  }

  // Gate: block all access when payment is pending or subscription has expired/failed.
  // Allow: billing pages (checkout/plans) and legal pages through the gate.
  if (subscriptionStatus === 'pending_payment' || subscriptionStatus === 'past_due') {
    const currentPath = window.location.pathname;
    const isBillingPage = ['/billing/checkout', '/billing/plans', '/billing'].includes(currentPath);
    const isLegalPage = ['/terms', '/privacy', '/refund-policy'].some(p => currentPath === p);
    if (!isBillingPage && !isLegalPage) {
      return <PaymentRequiredPage />;
    }
  }

  return <>{children}</>;
};

// ---------------------------------------------------------------
// PaymentRequiredPage — full standalone page when payment is
// pending or subscription has expired. No sidebar/navigation.
// ---------------------------------------------------------------
const PaymentRequiredPage: React.FC = () => {
  const { subscriptionStatus, pendingPlanId, pendingPeriod, setSubscriptionStatus, logout } = useAuth();
  const navigate = useNavigate();
  const isPastDue = subscriptionStatus === 'past_due';

  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [fees, setFees] = useState<any>(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(true);
  const [userCurrency, setUserCurrency] = useState('USD');
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(false);

  // Use stored period if monthly, otherwise keep yearly default
  useEffect(() => {
    if (pendingPeriod === 'monthly') setBillingPeriod('monthly');
  }, [pendingPeriod]);

  useEffect(() => {
    subscriptionAPI.listPlans().then(res => {
      const plansData = res.data?.data;
      const plansArray = Array.isArray(plansData) ? plansData : (plansData?.plans || []);
      const currency = Array.isArray(plansData) ? 'USD' : (plansData?.currency || 'USD');
      const paidPlans = plansArray.filter((p: any) => parseFloat(p.price_monthly) > 0);
      setPlans(paidPlans);
      setUserCurrency(currency);
      const preferred = pendingPlanId ? paidPlans.find((p: any) => p.id === pendingPlanId) : null;
      setSelectedPlan(preferred || paidPlans[0] || null);
    }).catch(() => {}).finally(() => setPlansLoading(false));
  }, [pendingPlanId]);

  useEffect(() => {
    if (selectedPlan) fetchFees();
  }, [selectedPlan, billingPeriod]);

  const fetchFees = async (code?: string) => {
    if (!selectedPlan) return;
    setFeesLoading(true);
    try {
      const res = await subscriptionAPI.previewFees({
        plan_id: selectedPlan.id,
        billing_period: billingPeriod,
        discount_code: code || undefined,
      });
      setFees(res.data.data);
    } catch {
      setFees(null);
    } finally {
      setFeesLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !selectedPlan) return;
    setDiscountValidating(true);
    try {
      const response = await discountAPI.validate(discountCode.trim(), selectedPlan.id, billingPeriod);
      if (response.data.data) {
        setDiscountApplied(true);
        toast.success(`Discount applied!`);
        await fetchFees(discountCode.trim());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid discount code');
      setDiscountApplied(false);
    } finally {
      setDiscountValidating(false);
    }
  };

  const handleRemoveDiscount = async () => {
    setDiscountApplied(false);
    setDiscountCode('');
    await fetchFees();
  };

  const handlePay = async () => {
    if (!selectedPlan) return;
    setProcessing(true);
    try {
      const response = await subscriptionAPI.subscribe({
        plan_id: selectedPlan.id,
        billing_period: billingPeriod,
        gateway: 'tap',
        payment_token: 'pending',
        discount_code: discountApplied ? discountCode.trim() : undefined,
      });
      const result = response.data.data;
      if (result.redirect_url) {
        setSubscriptionStatus('pending_payment', selectedPlan.id, billingPeriod);
        window.location.href = result.redirect_url;
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to initiate payment. Please try again.');
      setProcessing(false);
    }
  };

  const handleDowngrade = async () => {
    setDowngrading(true);
    try {
      const plansRes = await subscriptionAPI.listPlans();
      const plansData = plansRes.data?.data;
      const all = Array.isArray(plansData) ? plansData : (plansData?.plans || []);
      const freePlan = all.find((p: any) => parseFloat(p.price_monthly) <= 0);
      if (freePlan) await subscriptionAPI.changePlan(freePlan.id);
      setSubscriptionStatus('free', null, null);
      toast.success('Switched to Free plan.');
      navigate('/dashboard');
    } catch {
      toast.error('Failed to switch to free plan. Please try again.');
      setDowngrading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const activeCurrency = fees?.currency || userCurrency;
  const decimals = ['KWD', 'BHD', 'OMR'].includes(activeCurrency) ? 3 : 2;
  const rawPrice = fees
    ? Number(fees.total_after_vat)
    : selectedPlan
      ? Number(billingPeriod === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly)
      : 0;
  const displayPrice = rawPrice.toFixed(decimals);

  const yearlySavingPct = selectedPlan && selectedPlan.price_yearly && selectedPlan.price_monthly
    ? Math.round(((parseFloat(selectedPlan.price_monthly) * 12 - parseFloat(selectedPlan.price_yearly)) / (parseFloat(selectedPlan.price_monthly) * 12)) * 100)
    : 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, fontSize: '1.15rem', color: '#1e293b' }}>
          <i className="bi bi-shield-check text-primary me-2"></i>CloudAyn
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
          <i className="bi bi-box-arrow-right me-1"></i>Sign Out
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
        {plansLoading ? (
          <div className="spinner-border text-primary" role="status"></div>
        ) : (
          <div style={{ maxWidth: 560, width: '100%' }}>
            {/* Title */}
            <div className="text-center mb-4">
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 56, height: 56, borderRadius: '50%',
                background: isPastDue ? '#fee2e2' : '#eff6ff', marginBottom: '1rem',
              }}>
                <i className={`bi ${isPastDue ? 'bi-exclamation-circle-fill' : 'bi-credit-card-fill'}`}
                  style={{ fontSize: 24, color: isPastDue ? '#dc2626' : '#3b82f6' }}></i>
              </span>
              <h3 className="fw-bold mb-1">
                {isPastDue ? 'Renew Your Subscription' : 'Complete Your Payment'}
              </h3>
              <p className="text-muted mb-0">
                {isPastDue
                  ? 'Your subscription has expired. Renew to restore full access.'
                  : 'Your payment was not completed. Select a plan to get started.'}
              </p>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body p-4">
                {/* Plan selector */}
                {plans.length > 1 && (
                  <div className="mb-4">
                    <label className="form-label fw-semibold small text-uppercase text-muted mb-2">Select Plan</label>
                    <div className="d-flex gap-2 flex-wrap">
                      {plans.map((p: any) => (
                        <button
                          key={p.id}
                          className={`btn btn-sm ${selectedPlan?.id === p.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setSelectedPlan(p)}
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Billing period toggle */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small text-uppercase text-muted mb-2">Billing Period</label>
                  <div className="d-flex gap-2">
                    <button
                      className={`btn btn-sm flex-fill ${billingPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setBillingPeriod('monthly')}
                    >
                      Monthly
                    </button>
                    <button
                      className={`btn btn-sm flex-fill ${billingPeriod === 'yearly' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setBillingPeriod('yearly')}
                    >
                      Yearly
                      {yearlySavingPct > 0 && (
                        <span className="badge bg-success ms-1" style={{ fontSize: '0.65rem' }}>Save {yearlySavingPct}%</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Price breakdown */}
                {feesLoading ? (
                  <div className="text-center py-2 mb-4">
                    <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                    <small className="text-muted">Calculating...</small>
                  </div>
                ) : (fees || selectedPlan) ? (
                  <div className="bg-light rounded p-3 mb-4">
                    {fees ? (
                      <>
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Subtotal</span>
                          <span>{fees.currency} {Number(fees.subtotal).toFixed(decimals)}</span>
                        </div>
                        {fees.discount_amount > 0 && (
                          <div className="d-flex justify-content-between small mb-1 text-success">
                            <span>Discount ({fees.discount_name})</span>
                            <span>-{fees.currency} {Number(fees.discount_amount).toFixed(decimals)}</span>
                          </div>
                        )}
                        {fees.vat_applicable && (
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">VAT ({fees.vat_percentage}%)</span>
                            <span>{fees.currency} {Number(fees.vat_amount).toFixed(decimals)}</span>
                          </div>
                        )}
                        <div className="d-flex justify-content-between fw-bold border-top mt-2 pt-2">
                          <span>Total</span>
                          <span className="text-primary">{fees.currency} {Number(fees.total_after_vat).toFixed(decimals)}</span>
                        </div>
                        {fees.vat_applicable && (
                          <div className="text-center mt-1">
                            <small className="text-muted">Inclusive of {fees.vat_percentage}% VAT</small>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="d-flex justify-content-between fw-bold">
                        <span>Total</span>
                        <span className="text-primary">{userCurrency} {displayPrice}</span>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Discount code */}
                <div className="mb-4">
                  <label className="form-label small fw-semibold">Discount Code</label>
                  {discountApplied ? (
                    <div className="d-flex align-items-center justify-content-between bg-success bg-opacity-10 border border-success rounded p-2">
                      <span className="small text-success">
                        <i className="bi bi-check-circle-fill me-1"></i>
                        {fees?.discount_name || discountCode} applied
                        {fees?.discount_amount > 0 && ` (-${fees.currency} ${Number(fees.discount_amount).toFixed(decimals)})`}
                      </span>
                      <button className="btn btn-link btn-sm text-danger p-0" onClick={handleRemoveDiscount}>
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter discount code"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === 'Enter' && handleApplyDiscount()}
                      />
                      <button
                        className="btn btn-outline-primary"
                        onClick={handleApplyDiscount}
                        disabled={discountValidating || !discountCode.trim()}
                      >
                        {discountValidating
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : 'Apply'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Pay button */}
                <button
                  className="btn btn-primary btn-lg w-100 mb-3"
                  onClick={handlePay}
                  disabled={processing || !selectedPlan}
                >
                  {processing ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Redirecting...</>
                  ) : (
                    <><i className="bi bi-lock-fill me-2"></i>Pay {activeCurrency} {displayPrice} Securely</>
                  )}
                </button>

                {/* Security note */}
                <div className="text-center mb-3">
                  <small className="text-muted">
                    <i className="bi bi-shield-lock me-1"></i>
                    Processed by a secure payment gateway &mdash; we do not store your card information.
                  </small>
                </div>

                <hr className="my-3" />

                {/* Downgrade */}
                <div className="text-center">
                  <p className="text-muted small mb-2">Want to continue without a paid plan?</p>
                  <button
                    className="btn btn-link btn-sm text-muted p-0"
                    onClick={handleDowngrade}
                    disabled={downgrading}
                  >
                    {downgrading
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Processing...</>
                      : 'Continue with Free Plan'}
                  </button>
                </div>
              </div>
            </div>

            <div className="text-center mt-3">
              <small className="text-muted">
                Need help? <a href="mailto:support@cloudayn.com" className="text-muted">Contact support</a>
              </small>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Public Route - redirect to dashboard if already logged in
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) return <LoadingSpinner />;

  if (user) {
    // Check for a stored post-login redirect (e.g. signup → checkout)
    const postLoginRedirect = sessionStorage.getItem('post_login_redirect');
    if (postLoginRedirect) {
      sessionStorage.removeItem('post_login_redirect');
      return <Navigate to={postLoginRedirect} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Fetch Google Client ID from login-methods and always provide GoogleOAuthProvider.
// Using a placeholder ensures useGoogleLogin() never crashes from missing context —
// the Google button only appears after loginMethods confirms google is enabled.
const GoogleOAuthWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [googleClientId, setGoogleClientId] = useState('placeholder');

  useEffect(() => {
    authAPI.getLoginMethods()
      .then((res) => {
        if (res.data.google_client_id) {
          setGoogleClientId(res.data.google_client_id);
        }
      })
      .catch(() => { /* login methods fetch failed, OAuth buttons won't show */ });
  }, []);

  return <GoogleOAuthProvider clientId={googleClientId}>{children}</GoogleOAuthProvider>;
};

// Locale slug routes for country-specific landing pages: /bh-en, /sa-en, etc.
const LOCALE_SLUGS = ['bh-en', 'sa-en', 'ae-en', 'kw-en', 'om-en', 'qa-en', 'gb-en', 'us-en', 'eu-en'];

const LocaleRoute: React.FC = () => {
  const { countryLang } = useParams<{ countryLang: string }>();
  if (countryLang && LOCALE_SLUGS.includes(countryLang)) {
    // Store locale preference
    localStorage.setItem('user_locale', countryLang);
    return <PublicRoute><LandingPage /></PublicRoute>;
  }
  return <Navigate to="/" replace />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <GoogleOAuthWrapper>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><ModernLoginPage /></PublicRoute>} />
            <Route path="/signup" element={<PublicRoute><ModernSignupPage /></PublicRoute>} />
            <Route path="/verify-otp" element={<PublicRoute><OTPVerificationPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/invitation/:token" element={<InvitationAcceptPage />} />

            {/* Legal Pages (public, accessible to all users) */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />

            {/* Blog / Insights (public) */}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Protected Routes - Setup */}
            <Route path="/setup/organization" element={<ProtectedRoute><OrganizationSetupPage /></ProtectedRoute>} />
            <Route path="/setup/asset" element={<ProtectedRoute><ModernAssetSetupPage /></ProtectedRoute>} />

            {/* Analytics Routes */}
            <Route path="/analytics/summary" element={<ProtectedRoute requiresAsset={true}><SummaryPage /></ProtectedRoute>} />
            <Route path="/analytics/pageviews" element={<ProtectedRoute requiresAsset={true}><DetailedAnalyticsPage /></ProtectedRoute>} />
            <Route path="/analytics/detailed" element={<ProtectedRoute requiresAsset={true}><DetailedAnalyticsPage /></ProtectedRoute>} />
            <Route path="/analytics/page/:pageUrl" element={<ProtectedRoute requiresAsset={true}><PageDetailView /></ProtectedRoute>} />
            <Route path="/analytics/transactions" element={<ProtectedRoute requiresAsset={true}><TransactionsPage /></ProtectedRoute>} />
            <Route path="/analytics/report" element={<ProtectedRoute requiresAsset={true}><ReportPage /></ProtectedRoute>} />
            <Route path="/analytics/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
            <Route path="/analytics/campaigns/:id" element={<ProtectedRoute><CampaignDetailPage /></ProtectedRoute>} />
            <Route path="/analytics/visitor-log" element={<ProtectedRoute requiresAsset={true}><VisitorLogPage /></ProtectedRoute>} />
            <Route path="/analytics/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />

            {/* App Analytics (mobile SDK) */}
            <Route path="/app-analytics" element={<Navigate to="/app-analytics/overview" replace />} />
            <Route path="/app-analytics/setup" element={<ProtectedRoute requiresAsset={true}><AppSetupPage /></ProtectedRoute>} />
            <Route path="/app-analytics/overview" element={<ProtectedRoute requiresAsset={true}><AppOverviewPage /></ProtectedRoute>} />
            <Route path="/app-analytics/funnel" element={<ProtectedRoute requiresAsset={true}><AppFunnelPage /></ProtectedRoute>} />
            <Route path="/app-analytics/users" element={<ProtectedRoute requiresAsset={true}><AppUsersPage /></ProtectedRoute>} />
            <Route path="/app-analytics/users/:id" element={<ProtectedRoute requiresAsset={true}><AppUserTimelinePage /></ProtectedRoute>} />
            {/* Customers only — docs and SDK links are not public */}
            <Route path="/docs/app-integration" element={<ProtectedRoute><AppIntegrationDocsPage /></ProtectedRoute>} />

            {/* Monitoring Routes */}
            <Route path="/monitoring/live" element={<ProtectedRoute requiresAsset={true}><LiveMonitoringPage /></ProtectedRoute>} />
            <Route path="/monitoring/performance" element={<ProtectedRoute requiresAsset={true}><PerformanceReportPage /></ProtectedRoute>} />
            <Route path="/monitoring/hub" element={<ProtectedRoute><LiveHubPage /></ProtectedRoute>} />

            {/* Security Routes */}
            <Route path="/security/summary" element={<ProtectedRoute requiresAsset={true}><SecuritySummaryPage /></ProtectedRoute>} />
            <Route path="/security/scanning" element={<ProtectedRoute requiresAsset={true}><SecurityScanningPage /></ProtectedRoute>} />
            <Route path="/security/scan-progress" element={<ProtectedRoute requiresAsset={true}><SecurityScanProgressPage /></ProtectedRoute>} />
            <Route path="/security/vulnerabilities" element={<ProtectedRoute requiresAsset={true}><SecurityVulnerabilitiesPage /></ProtectedRoute>} />
            <Route path="/security/results" element={<ProtectedRoute requiresAsset={true}><ScanResultsPage /></ProtectedRoute>} />
            <Route path="/security/results/:scanId" element={<ProtectedRoute requiresAsset={true}><ScanResultsPage /></ProtectedRoute>} />
            <Route path="/security/ai-advisor" element={<ProtectedRoute requiresAsset={true}><AISecurityAdvisorPage /></ProtectedRoute>} />
            <Route path="/security/ai-audit" element={<ProtectedRoute requiresAsset={true}><AiSecurityAuditPage /></ProtectedRoute>} />
            <Route path="/security/ai-assistant" element={<ProtectedRoute requiresAsset={true}><AiAssistantPage /></ProtectedRoute>} />
            <Route path="/security/code-scan" element={<ProtectedRoute requiresAsset={true}><CodeScanDashboardPage /></ProtectedRoute>} />
            <Route path="/security/code-scan/targets" element={<ProtectedRoute requiresAsset={true}><CodeScanTargetsPage /></ProtectedRoute>} />
            <Route path="/security/code-scan/results/:id" element={<ProtectedRoute requiresAsset={true}><CodeScanResultDetailPage /></ProtectedRoute>} />

            {/* Integration Routes */}
            <Route path="/integration" element={<ProtectedRoute requiresAsset={true}><IntegrationPage /></ProtectedRoute>} />
            {/* Share Score & Data */}
            <Route path="/share/widget" element={<ProtectedRoute requiresAsset={true}><ShareDataPage /></ProtectedRoute>} />

            {/* User Management Routes */}
            <Route path="/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />

            {/* Organisation Routes */}
            <Route path="/organisation" element={<ProtectedRoute><OrganisationProfilePage /></ProtectedRoute>} />
            <Route path="/organisation/register" element={<ProtectedRoute><RegisterOrganisationPage /></ProtectedRoute>} />

            {/* Assets Routes */}
            <Route path="/assets" element={<ProtectedRoute><AssetsListPage /></ProtectedRoute>} />
            <Route path="/assets/:id" element={<ProtectedRoute><AssetDetailPage /></ProtectedRoute>} />

            {/* Server Monitoring Routes */}
            <Route path="/servers" element={<ProtectedRoute requiresAsset={true}><ServersListPage /></ProtectedRoute>} />
            <Route path="/servers/register" element={<ProtectedRoute requiresAsset={true}><ServerRegistrationPage /></ProtectedRoute>} />
            <Route path="/servers/:id" element={<ProtectedRoute requiresAsset={true}><ServerDetailPage /></ProtectedRoute>} />
            <Route path="/servers/:id/files" element={<ProtectedRoute requiresAsset={true}><FileBrowserPage /></ProtectedRoute>} />
            <Route path="/endpoints" element={<ProtectedRoute requiresAsset={true}><EndpointsListPage /></ProtectedRoute>} />
            <Route path="/endpoints/add" element={<ProtectedRoute requiresAsset={true}><EndpointAddPage /></ProtectedRoute>} />
            <Route path="/endpoints/policy" element={<ProtectedRoute requiresAsset={true}><EndpointPolicyPage /></ProtectedRoute>} />
            <Route path="/endpoints/threats" element={<ProtectedRoute requiresAsset={true}><EndpointThreatsPage /></ProtectedRoute>} />
            <Route path="/endpoints/:id" element={<ProtectedRoute requiresAsset={true}><EndpointDetailPage /></ProtectedRoute>} />

            {/* Alerts Routes */}
            <Route path="/alerts/history" element={<ProtectedRoute requiresAsset={true}><AlertSendHistoryPage /></ProtectedRoute>} />

            {/* Email Security Monitoring Routes */}
            <Route path="/email-monitor/dashboard" element={<ProtectedRoute requiresAsset={true}><EmailMonitorDashboardPage /></ProtectedRoute>} />
            <Route path="/email-monitor/dns-records" element={<ProtectedRoute requiresAsset={true}><EmailDnsRecordsPage /></ProtectedRoute>} />
            <Route path="/email-monitor/dns-history" element={<ProtectedRoute requiresAsset={true}><EmailDnsHistoryPage /></ProtectedRoute>} />
            <Route path="/email-monitor/blacklist" element={<ProtectedRoute requiresAsset={true}><EmailBlacklistPage /></ProtectedRoute>} />

            {/* Event Points Routes */}
            <Route path="/events" element={<Navigate to="/analytics/events" replace />} />
            <Route path="/events/:id" element={<ProtectedRoute><EventPointDetailPage /></ProtectedRoute>} />

            {/* SEO Routes */}
            <Route path="/seo" element={<Navigate to="/seo/summary" replace />} />
            <Route path="/seo/summary" element={<ProtectedRoute requiresAsset={true}><SeoSummaryPage /></ProtectedRoute>} />
            <Route path="/seo/scanner" element={<ProtectedRoute requiresAsset={true}><SeoScannerPage /></ProtectedRoute>} />
            <Route path="/seo/optimizer" element={<ProtectedRoute requiresAsset={true}><SeoOptimizerPage /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute requiresAsset={true}><NotificationSettingsPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingOverviewPage /></ProtectedRoute>} />
            <Route path="/onboarding/:assetId" element={<ProtectedRoute><OnboardingAssetPage /></ProtectedRoute>} />
            <Route path="/seo/notifications" element={<Navigate to="/settings/notifications" replace />} />
            <Route path="/seo/keywords" element={<ProtectedRoute requiresAsset={true}><KeywordMonitorPage /></ProtectedRoute>} />
            <Route path="/seo/for-ai" element={<ProtectedRoute requiresAsset={true}><SeoForAIPage /></ProtectedRoute>} />

            {/* Feedback Routes */}
            <Route path="/feedback" element={<ProtectedRoute requiresAsset={true}><FeedbackPage /></ProtectedRoute>} />
            <Route path="/integration/feedback" element={<ProtectedRoute requiresAsset={true}><FeedbackWidgetPage /></ProtectedRoute>} />

            {/* Billing Routes */}
            <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/billing/plans" element={<ProtectedRoute><PlansPage /></ProtectedRoute>} />
            <Route path="/billing/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
            <Route path="/billing/payments" element={<ProtectedRoute><PaymentHistoryPage /></ProtectedRoute>} />
            <Route path="/billing/receipt/:id" element={<ProtectedRoute><ReceiptPage /></ProtectedRoute>} />

            {/* Home Dashboard */}
            <Route path="/dashboard" element={<ProtectedRoute requiresAsset={true}><MainDashboardPage /></ProtectedRoute>} />

            {/* Country/language slug routes for public landing page: /bh-en, /sa-en, /ae-en, etc. */}
            <Route path="/:countryLang" element={<LocaleRoute />} />

            {/* Redirect unknown routes to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              success: { style: { background: '#059669', color: '#fff' } },
              error: { style: { background: '#dc2626', color: '#fff' } },
            }}
          />
        </div>
      </Router>
      </GoogleOAuthWrapper>
    </AuthProvider>
  );
};

export default App;